import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { useLookups } from '../../context/LookupContext';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import { hasColumnFilters, NO_MATCH_LIST, SEARCH_HINT } from '../../utils/listSearch';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { useListTrunk } from '../../hooks/useListTrunk';
import { useLocation } from 'react-router-dom';
import { entryMemoryKey, loadView, saveView } from '../../utils/viewMemory';
import NoMatchState from '../../components/common/NoMatchState';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import TaskTransferModal from './TaskTransferModal';
import {
  RefreshCw, Plus, X, Pencil, Trash2, Save,
  ChevronLeft, ChevronRight,
  ClipboardCheck, GripVertical, List, Table, FilterX, ListFilter, Upload
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp } from '../../utils/dateTime';

// `claudeSummary` is an AI-written body rendered on the detail panel and was not searchable —
// ~208K characters against ~237K in the details. `id` is the grid's first column, so a task was
// findable by every field except the number on screen. The *Name fields are what both views
// RENDER for owner and originator, while the old predicate matched only the slug.
// What search reads, labelled as the user sees it. The labels double as field: prefixes and feed
// the Advanced Search builder's field list.
const SEARCH_FIELD_LABELS = [
  { key: 'id', label: 'ID' }, { key: 'task', label: 'Task' }, { key: 'details', label: 'Details' },
  { key: 'claudeSummary', label: 'Claude Summary' }, { key: 'source', label: 'Source' },
  { key: 'status', label: 'Status' }, { key: 'category', label: 'Priority' }, { key: 'entryType', label: 'Type' },
  { key: 'owner', label: 'Owner' }, { key: 'ownerName', label: 'Owner Name' },
  { key: 'originator', label: 'Originator' }, { key: 'originatorName', label: 'Originator Name' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);

// Person columns filter on the display name the API resolves (ownerName / originatorName), so
// the filter list reads the same "Last, First" the cells show instead of login handles. A view
// saved before that kept handle values under owner / originator, which would now match nothing
// while still counting as an active filter, so those keys are dropped on load.
const dropHandleFilters = (filters) => {
  if (!filters) return filters;
  const { owner, originator, ...rest } = filters; // eslint-disable-line no-unused-vars
  return rest;
};

// Vocabularies live in AppCfg_LookupValues, not here. The same rows drive the API
// validator, so a picker can no longer offer a value the API rejects.
const LK_STATUS = 'Task_Status';
const LK_TYPE = 'Task_Type';
const LK_PRIORITY = 'Task_Priority';

// Transfer to Pre-Prod is a Prod action; development builds show it too so the popup can be
// exercised on a dev VM, where the API refuses the transfer itself (source and target are one DB).
const CAN_TRANSFER_ENV = !!window.__config__?.IS_PROD || process.env.NODE_ENV === 'development';

// Open work, including rows transferred from Prod that are waiting for triage.
const DEFAULT_STATUS_FILTER = ['From Prod', 'Pending', 'In Progress', 'Validate', 'Retry'];

// 'Archived' is written only by the archive action; the API rejects it on a normal
// update, so it must not appear in a picker either.
const GUARDED_STATUS = 'Archived';

const TaskLogPage = () => {
  // ── Shared ────────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // How this page was left in THIS history entry (utils/viewMemory): Back restores it, a nav
  // click or new link is a new entry and starts clean. Read once per mount.
  const location = useLocation();
  const memoryKey = entryMemoryKey(location, 'task-log');
  const [saved] = useState(() => loadView(memoryKey));
  const [viewMode, setViewMode] = useState(saved?.view === 'datasheet' ? 'datasheet' : 'list');
  const [searchTerm, setSearchTerm] = useState(saved?.search || '');
  const { notify } = useNotifications();
  const { currentUser, isSysAdmin } = useAuth();
  const { getValues } = useLookups();
  const statusOptions = getValues(LK_STATUS).filter(v => v !== GUARDED_STATUS);
  const typeOptions = getValues(LK_TYPE);
  const priorityOptions = getValues(LK_PRIORITY);
  // Display_Order is the ranking, so position in the lookup IS the sort key.
  const priorityRank = (v) => {
    const i = priorityOptions.indexOf(v);
    return i === -1 ? 99 : i;
  };
  const [roster, setRoster] = useState([]);
  // Originator resolves against a wider set than Owner: a bug reporter must stay
  // selectable on the row they raised without becoming assignable for dev work.
  const [originatorRoster, setOriginatorRoster] = useState([]);
  // The roster is authoritative for the current user's slug. The email-derived value
  // only covers the first render, before the roster request comes back.
  const currentUserSlug =
    roster.find(m => m.email?.toLowerCase() === currentUser?.email?.toLowerCase())?.slug
    || currentUser?.email?.split('@')[0]?.split('.')[0]?.toLowerCase()
    || null;

  // ── List view ─────────────────────────────────────────────────────────────
  const [pickedTask, setSelectedTask] = useState(saved?.picked != null ? { id: saved.picked } : null);
  const [dsSort, setDsSort] = useState(saved?.sort?.key ? saved.sort : null); // header sort, shared by both views
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [listCollapsed, setListCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // ── Add task modal ────────────────────────────────────────────────────────
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ task: '', details: '', source: 'Task Log', status: 'Pending', owner: currentUserSlug, originator: currentUserSlug, category: '', entryType: 'Task' });

  // ── Shared filters (drive both list and datasheet) ────────────────────────
  const [dsFilters, setDsFilters] = useState(dropHandleFilters(saved?.filters) || { status: DEFAULT_STATUS_FILTER });

  // ── Datasheet state ───────────────────────────────────────────────────────
  const [dsEditMode, setDsEditMode] = useState(false);
  const [dsEdits, setDsEdits] = useState({});
  const [showDescModal, setShowDescModal] = useState(false);
  const [descModalTask, setDescModalTask] = useState(null);
  const [descLocal, setDescLocal] = useState({ details: '' });

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devFilesApi.getTaskLogJson();
      setTasks(response.data.tasks || []);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  // Badge classes name a colour, not a meaning — the visible label carries that.
  // An unrecognised value still renders as a badge rather than bare text.
  const getStatusClass = (status) => {
    if (status === 'From Prod')   return 'stui-badge-teal';
    if (status === 'Pending')     return 'stui-badge-amber';
    if (status === 'In Progress') return 'stui-badge-orange';
    if (status === 'Validate')    return 'stui-badge-blue';
    if (status === 'Retry')       return 'stui-badge-purple';
    if (status === 'Complete')    return 'stui-badge-green';
    return 'stui-badge-gray';
  };

  const getEntryTypeClass = (t) => {
    if (t === 'Bug')     return 'stui-badge-red';
    if (t === 'Feature') return 'stui-badge-blue';
    return 'stui-badge-gray';
  };

  const getPriorityClass = (c) => {
    if (c === 'Critical') return 'stui-badge-red';
    if (c === 'High')     return 'stui-badge-orange';
    if (c === 'Medium')   return 'stui-badge-amber';
    return 'stui-badge-gray';
  };

  const formatOwnerName = (slug) => {
    if (!slug) return 'Unassigned';
    const hit = (list) => list.find(m => m.slug?.toLowerCase() === slug.toLowerCase());
    const member = hit(roster) || hit(originatorRoster);
    return member ? member.displayName : slug;
  };

  // Task_Status values are proper case ('Pending', 'In Progress') and the API validates them
  // case-sensitively, so comparing against a lowercase literal silently counted nothing.
  const getStatusCount = (status) => {
    if (!tasks.length) return 0;
    return tasks.filter(t => t.status === status).length;
  };

  useEffect(() => {
    devFilesApi.getDevTrackRoster()
      .then(res => setRoster(res.data?.members || []))
      .catch(() => setRoster([]));
    devFilesApi.getDevTrackOriginators()
      .then(res => setOriginatorRoster(res.data?.members || []))
      .catch(() => setOriginatorRoster([]));
  }, []);

  // ── Filtered data — one trunk, both views ─────────────────────────────────
  // The search predicate used to be written twice, once for each view, 20 lines apart. The list
  // path also compared raw values while the grid compared stringified ones, so a numeric column
  // like `id` could never match on one path and always could on the other.
  // One trunk (sort -> search -> filters) for both views; the detail pane never shows a row the
  // list is not showing. See hooks/useListTrunk.
  const { searched: searchedTasks, filtered: filteredTasks, selected: selectedTask } = useListTrunk({
    rows: tasks, query: searchTerm, searchFields: SEARCH_FIELDS, labels: SEARCH_FIELD_LABELS,
    filters: dsFilters, sortConfig: dsSort, picked: pickedTask, keep: isEditing,
  });

  const dsDisplayData = filteredTasks;

  // A new history entry under a mounted page — clicking this page's own nav item — starts clean
  // (or restores, if the entry was visited before). A remount already reads it via useState.
  const entryKeyRef = useRef(memoryKey);
  useEffect(() => {
    if (entryKeyRef.current === memoryKey) return;
    entryKeyRef.current = memoryKey;
    const s = loadView(memoryKey);
    setViewMode(s?.view === 'datasheet' ? 'datasheet' : 'list');
    setSearchTerm(s?.search || '');
    setDsFilters(dropHandleFilters(s?.filters) || { status: DEFAULT_STATUS_FILTER });
    setDsSort(s?.sort?.key ? s.sort : null);
    setSelectedTask(s?.picked != null ? { id: s.picked } : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryKey]);

  // Remember this page's state against the current history entry.
  useEffect(() => {
    saveView(memoryKey, { view: viewMode, search: searchTerm, filters: dsFilters, sort: dsSort, picked: (selectedTask ?? pickedTask)?.id ?? null });
  }, [memoryKey, viewMode, searchTerm, dsFilters, dsSort, selectedTask, pickedTask]);

  const fieldDirty = (field) => isEditing && editForm && selectedTask && editForm[field] !== selectedTask[field];

  const hasChanges = isEditing && editForm && selectedTask && (
    editForm.task !== selectedTask.task ||
    editForm.details !== selectedTask.details ||
    editForm.source !== selectedTask.source ||
    editForm.owner !== selectedTask.owner ||
    editForm.originator !== selectedTask.originator ||
    editForm.category !== selectedTask.category ||
    editForm.entryType !== selectedTask.entryType ||
    editForm.status !== selectedTask.status
  );

  const dsIsDirty = Object.keys(dsEdits).length > 0;
  const dsHasFilters = hasColumnFilters(dsFilters);
  const dsFilteredCount = filteredTasks.length;

  const dsColumns = useMemo(() => [
    { key: 'id',       fieldName: 'id',       label: 'ID',       width: 60  },
    { key: 'task',     fieldName: 'task',     label: 'Task',     width: 460 },
    { key: 'status',   fieldName: 'status',   label: 'Status',   width: 120,
      formatter: (v) => v ? <span className={`${getStatusClass(v)}`}>{v}</span> : null },
    // key is the resolved name (what the filter lists and matches), fieldName the handle (what edits write).
    { key: 'ownerName', fieldName: 'owner', label: 'Owner', width: 100,
      renderCell: (row) => row.ownerName || formatOwnerName(row.owner) },
    { key: 'originatorName', fieldName: 'originator', label: 'Originator', width: 110,
      renderCell: (row) => row.originatorName || formatOwnerName(row.originator) },
    { key: 'entryType', fieldName: 'entryType', label: 'Type', width: 90,
      formatter: (v) => v ? <span className={getEntryTypeClass(v)}>{v}</span> : null },
    { key: 'category', fieldName: 'category', label: 'Priority', width: 90,
      formatter: (v) => v ? <span className={getPriorityClass(v)}>{v}</span> : null,
      sortValue: (v) => priorityRank(v) },
    { key: 'source',   fieldName: 'source',   label: 'Source',   width: 150 },
  ], [roster, priorityOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Editable cell renderer for datasheet edit mode
  const renderDsCell = (row, col, displayValue, isEdited) => {
    switch (col.fieldName) {
      case 'task':
        return <input className="stui-ds-cell-input stui-w-full" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'task', e.target.value)} />;
      case 'status':
        return (
          <StuiSelect className="stui-ds-cell-input" value={displayValue ?? 'Pending'} onChange={e => handleDsEdit(row.id, 'status', e.target.value)}>
            {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </StuiSelect>
        );
      case 'owner':
        return (
          <StuiSelect className="stui-ds-cell-input" value={(isEdited ? displayValue : row.owner) ?? ''} onChange={e => handleDsEdit(row.id, 'owner', e.target.value || null)}>
            <option value="">Unassigned</option>
            {roster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
          </StuiSelect>
        );
      case 'originator':
        return (
          <StuiSelect className="stui-ds-cell-input" value={(isEdited ? displayValue : row.originator) ?? ''} onChange={e => handleDsEdit(row.id, 'originator', e.target.value || null)}>
            <option value="">Unassigned</option>
            {originatorRoster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
          </StuiSelect>
        );
      case 'entryType':
        return (
          <StuiSelect className="stui-ds-cell-input" value={displayValue ?? 'Task'} onChange={e => handleDsEdit(row.id, 'entryType', e.target.value)}>
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </StuiSelect>
        );
      case 'category':
        return (
          <StuiSelect className="stui-ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'category', e.target.value || null)}>
            <option value="">--</option>
            {priorityOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </StuiSelect>
        );
      case 'source':
        return <input className="stui-ds-cell-input stui-w-full" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'source', e.target.value)} />;
      default:
        return displayValue;
    }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleAddTask = async () => {
    if (!newTask.task.trim()) return;
    try {
      setSaving(true);
      const res = await devFilesApi.createTask({
        task: newTask.task.trim(),
        status: newTask.status || 'pending',
        details: newTask.details?.trim() || null,
        source: newTask.source || 'Task Log',
        owner: newTask.owner || null,
        originator: newTask.originator || null,
        category: newTask.category || null,
        entryType: newTask.entryType || 'Task',
        claudeSummary: null
      });
      setNewTask({ task: '', details: '', source: 'Task Log', status: 'Pending', owner: currentUserSlug, originator: currentUserSlug, category: '', entryType: 'Task' });
      setShowAddTask(false);
      await loadTasks();
      setSelectedTask(res.data);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    if (!selectedTask) return;
    setEditForm({ ...selectedTask });
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); setEditForm({}); };

  const handleSave = async () => {
    if (!editForm?.task?.trim()) return;
    try {
      setSaving(true);
      await devFilesApi.updateTask(editForm.id, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadTasks();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = () => {
    if (!selectedTask) return;
    notify.confirm('Delete this task?', async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteTask(selectedTask.id);
        setSelectedTask(null);
        setIsEditing(false);
        setEditForm({});
        await loadTasks();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete task');
      } finally {
        setSaving(false);
      }
    });
  };

  const handleStatusAdvance = async (task, newStatus) => {
    try {
      setSaving(true);
      await devFilesApi.updateTask(task.id, { ...task, status: newStatus });
      await loadTasks();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  // ── Datasheet handlers ────────────────────────────────────────────────────
  const handleDsEdit = (id, field, value) => {
    setDsEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleDsCancel = () => { setDsEdits({}); setDsEditMode(false); };

  const handleDsSave = async () => {
    const dirtyIds = Object.keys(dsEdits);
    if (dirtyIds.length === 0) return;
    try {
      setSaving(true);
      for (const id of dirtyIds) {
        const original = tasks.find(t => t.id === parseInt(id));
        if (!original) continue;
        await devFilesApi.updateTask(original.id, { ...original, ...dsEdits[id] });
      }
      setDsEdits({});
      setDsEditMode(false);
      notify.success('Changes saved');
      await loadTasks();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openDescModal = (task, editMode) => {
    const merged = { ...task, ...(dsEdits[task.id] || {}) };
    setDescModalTask(task);
    setDescLocal({ details: merged.details || '' });
    setShowDescModal(true);
  };

  const handleDescApply = () => {
    if (!descModalTask) return;
    setDsEdits(prev => ({ ...prev, [descModalTask.id]: { ...(prev[descModalTask.id] || {}), ...descLocal } }));
    setShowDescModal(false);
  };

  const selectTask = (task) => {
    if (isEditing && hasChanges) {
      notify.confirm('Discard unsaved changes?', () => { cancelEditing(); setSelectedTask(task); });
    } else {
      cancelEditing();
      setSelectedTask(task);
    }
  };

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  return (
    <div className="stui-module-container">

      {/* ── Header ── */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <ClipboardCheck size={20} />
          <h1>Task Log</h1>
          <div className="stui-module-header-tabs">
            <button onClick={() => setViewMode('list')} className={`stui-tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`stui-tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
          <span className="stui-module-header-meta">
            {tasks.length} tasks &bull; {getStatusCount('Pending')} pending
          </span>
        </div>
        <div className="stui-module-header-actions">
          {/* List view CRUD */}
          {viewMode === 'list' && (
            <>
              {isEditing
                ? <button className="stui-crud-btn stui-delete-btn" onClick={handleDeleteTask} disabled={saving} title="Delete"><Trash2 size={18} /></button>
                : <button className="stui-crud-btn" onClick={() => setShowAddTask(true)} title="Add Task"><Plus size={18} /></button>
              }
              <button
                className={`stui-crud-btn ${!selectedTask && !isEditing ? 'disabled' : ''}`}
                onClick={isEditing ? cancelEditing : startEditing}
                disabled={!selectedTask && !isEditing}
                title={isEditing ? 'Cancel' : 'Edit'}
              >
                {isEditing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button
                className={`stui-crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`}
                onClick={handleSave}
                disabled={!isEditing || saving || !hasChanges}
                title="Save"
              ><Save size={18} /></button>
            </>
          )}

          {/* Datasheet controls */}
          {viewMode === 'datasheet' && (
            <>
              <button className="stui-crud-btn" onClick={dsEditMode ? handleDsCancel : () => setDsEditMode(true)} title={dsEditMode ? 'Cancel' : 'Edit'}>
                {dsEditMode ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`stui-crud-btn ${!dsIsDirty || saving ? 'disabled' : ''}`} onClick={handleDsSave} disabled={!dsIsDirty || saving} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Clear filter — always visible when filters active, both views */}
          {dsHasFilters && (
            <button onClick={() => setDsFilters({})} className="stui-clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{dsFilteredCount}/{tasks.length}</span>
            </button>
          )}

          {/* Search — persists across views */}
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
            <input
              className="stui-search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search tasks..."
              title={SEARCH_HINT}
            />
          </div>

          {/* 6-dot */}
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowAdvSearch(true); }}><ListFilter size={16} /><span>Advanced Search…</span></button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); loadTasks(); }}><RefreshCw size={16} /><span>Refresh</span></button>
                {CAN_TRANSFER_ENV && isSysAdmin && (
                  <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowTransfer(true); }}><Upload size={16} /><span>Transfer to Pre-Prod…</span></button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="stui-list-view">

        {/* ── List view ── */}
        {viewMode === 'list' && (
          <>
            {listCollapsed ? (
              <div className="stui-list-panel stui-collapsed">
                <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
              </div>
            ) : (
              <div className="stui-list-panel">
                <div className="stui-list-panel-header">
                  <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
                </div>
                <div className="stui-list-scroll">
                  {filteredTasks.length > 0 ? filteredTasks.map(task => (
                    <button key={task.id} onClick={() => selectTask(task)} className={`stui-list-item ${selectedTask?.id === task.id ? 'selected' : ''}`}>
                      <span className="stui-list-item-primary">{task.task}</span>
                      {/* Plain text, no badges: the list is for scanning whose it is and where it is, the
                          same line the Dev Plan list shows. Type, priority and originator are in the detail
                          panel, and the datasheet filters and sorts on them. */}
                      <span className="stui-list-item-secondary">
                        #{task.id} &middot; {formatOwnerName(task.owner)} &middot; {task.status}
                      </span>
                    </button>
                  )) : <div className="stui-list-empty">{tasks.length ? NO_MATCH_LIST : 'No tasks'}</div>}
                </div>
              </div>
            )}

            {/* Detail panel */}
            <div className="stui-detail-panel">
              {selectedTask ? (
                <div className="stui-detail-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                  {/* General Information — fixed height, no scroll */}
                  <div className="stui-shrink-0">
                    <div className="stui-detail-grid">

                      <div className="stui-col-24">
                        <div className="stui-detail-section-header">General Information</div>
                      </div>

                      <div className="stui-field-group stui-col-24">
                        <label>Task</label>
                        {isEditing
                          ? <input type="text" className={`stui-field-input${fieldDirty('task') ? ' dirty' : ''}`} value={editForm.task || ''} onChange={e => setEditForm({ ...editForm, task: e.target.value })} autoFocus />
                          : <input type="text" className="stui-field-input" value={selectedTask.task} readOnly onChange={() => {}} />
                        }
                      </div>

                      <div className="stui-field-group stui-col-8">
                        <label>Status</label>
                        {isEditing
                          ? <StuiSelect className={`stui-field-input${fieldDirty('status') ? ' dirty' : ''}`} value={editForm.status || 'pending'} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                            </StuiSelect>
                          : <span className={`${getStatusClass(selectedTask.status)}`}>{selectedTask.status}</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-8">
                        <label>Owner</label>
                        {isEditing
                          ? <StuiSelect className={`stui-field-input${fieldDirty('owner') ? ' dirty' : ''}`} value={editForm.owner || ''} onChange={e => setEditForm({ ...editForm, owner: e.target.value || null })}>
                              <option value="">Unassigned</option>
                              {roster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
                            </StuiSelect>
                          : <span className="stui-badge-owner">{formatOwnerName(selectedTask.owner)}</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-8">
                        <label>Originator</label>
                        {isEditing
                          ? <StuiSelect className={`stui-field-input${fieldDirty('originator') ? ' dirty' : ''}`} value={editForm.originator || ''} onChange={e => setEditForm({ ...editForm, originator: e.target.value || null })}>
                              <option value="">Unassigned</option>
                              {originatorRoster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
                            </StuiSelect>
                          : <span className="stui-badge-owner">{formatOwnerName(selectedTask.originator)}</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-8">
                        <label>Type</label>
                        {isEditing
                          ? <StuiSelect className={`stui-field-input${fieldDirty('entryType') ? ' dirty' : ''}`} value={editForm.entryType || 'task'} onChange={e => setEditForm({ ...editForm, entryType: e.target.value })}>
                              {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                            </StuiSelect>
                          : <span className={getEntryTypeClass(selectedTask.entryType)}>{selectedTask.entryType}</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-8">
                        <label>Priority</label>
                        {isEditing
                          ? <StuiSelect className={`stui-field-input${fieldDirty('category') ? ' dirty' : ''}`} value={editForm.category || ''} onChange={e => setEditForm({ ...editForm, category: e.target.value || null })}>
                              <option value="">Untriaged</option>
                              {priorityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                            </StuiSelect>
                          : selectedTask.category
                            ? <span className={getPriorityClass(selectedTask.category)}>{selectedTask.category}</span>
                            : <span className="stui-badge-gray">Untriaged</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-12">
                        <label>Source</label>
                        {isEditing
                          ? <input type="text" className={`stui-field-input${fieldDirty('source') ? ' dirty' : ''}`} value={editForm.source || ''} onChange={e => setEditForm({ ...editForm, source: e.target.value })} />
                          : <input type="text" className="stui-field-input" value={selectedTask.source || ''} readOnly onChange={() => {}} />
                        }
                      </div>

                      <div className="stui-field-group stui-col-6">
                        <label>Entered</label>
                        <input type="text" className="stui-field-input" value={formatTimestamp(selectedTask.entered)} readOnly onChange={() => {}} />
                      </div>

                      <div className="stui-field-group stui-col-6">
                        <label>ID</label>
                        <input type="text" className="stui-field-input" value={selectedTask.id} readOnly onChange={() => {}} />
                      </div>

                    </div>
                  </div>

                  {/* Details — fills remaining space */}
                  <div className="stui-detail-fill">
                    <div className="stui-detail-section-header">Details</div>
                    {isEditing
                      ? <textarea className={`stui-field-input stui-flex-1${fieldDirty('details') ? ' dirty' : ''}`} value={editForm.details || ''} onChange={e => setEditForm({ ...editForm, details: e.target.value })} style={{ resize: 'none', minHeight: 0 }} />
                      : <textarea className="stui-field-input stui-flex-1" value={selectedTask.details || ''} readOnly onChange={() => {}} style={{ resize: 'none', minHeight: 0, overflowY: 'auto' }} />
                    }
                  </div>

                  {/* Claude Summary — conditional, fixed height below details */}
                  {selectedTask.claudeSummary && (
                    <div className="stui-shrink-0" style={{ padding: '0 var(--spacing-md) var(--spacing-sm)' }}>
                      <div className="stui-detail-section-header">Claude Summary</div>
                      <textarea className="stui-field-input stui-w-full" value={selectedTask.claudeSummary} readOnly rows={4} onChange={() => {}} style={{ resize: 'none', overflowY: 'auto' }} />
                    </div>
                  )}

                  {/* Sprint action buttons — fixed at bottom.
                      Statuses are the proper-case Task_Status rows and the API validates them
                      case-sensitively, so the lowercase literals these tests and calls used
                      matched no task and would have been rejected had they ever fired. */}
                  {!isEditing && (
                    <div className="stui-action-bar">
                      {(selectedTask.status === 'Pending' || selectedTask.status === 'Retry') && (
                        <button className="stui-btn stui-btn-primary" onClick={() => handleStatusAdvance(selectedTask, 'In Progress')} disabled={saving}>Start</button>
                      )}
                      {selectedTask.status === 'In Progress' && (
                        <button className="stui-btn stui-btn-primary" onClick={() => handleStatusAdvance(selectedTask, 'Validate')} disabled={saving}>Mark Validate</button>
                      )}
                      {selectedTask.status === 'Validate' && (
                        <>
                          <button className="stui-btn stui-btn-secondary" onClick={() => handleStatusAdvance(selectedTask, 'Retry')} disabled={saving}>Retry</button>
                          <button className="stui-btn stui-btn-primary" onClick={() => handleStatusAdvance(selectedTask, 'Complete')} disabled={saving}>Complete</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="stui-detail-empty">
                  {tasks.length > 0 && filteredTasks.length === 0 ? (
                    <NoMatchState hasSearch={!!searchTerm} hasFilters={dsHasFilters} onClearSearch={() => setSearchTerm('')} onClearFilters={() => setDsFilters({})} />
                  ) : <p>Select a task to view details</p>}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Datasheet view ── */}
        {viewMode === 'datasheet' && (
          <div className="stui-datasheet-view">
            <FilterableDataGrid
              data={dsDisplayData}
              allData={searchedTasks}
              sortConfig={dsSort || { key: null, direction: null }}
              onSortChange={setDsSort}
              columns={dsColumns}
              filters={dsFilters}
              setFilters={setDsFilters}
              editModeEnabled={dsEditMode}
              editedData={dsEdits}
              renderEditableCell={renderDsCell}
              onRowDoubleClick={(row) => openDescModal(row, dsEditMode)}
            />
          </div>
        )}
      </div>

      {/* ── Add Task Modal ── */}
      {showAddTask && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>New Task</h3>
              <button className="stui-modal-close" onClick={() => setShowAddTask(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-detail-grid">

                <div className="stui-col-24">
                  <div className="stui-detail-section-header">General Information</div>
                </div>

                <div className="stui-field-group stui-col-24">
                  <label>Task</label>
                  <input type="text" className="stui-field-input" value={newTask.task}
                    onChange={e => setNewTask({ ...newTask, task: e.target.value })}
                    placeholder="Short description of the task" autoFocus />
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Status</label>
                  <StuiSelect className="stui-field-input" value={newTask.status}
                    onChange={e => setNewTask({ ...newTask, status: e.target.value })}>
                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </StuiSelect>
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Owner</label>
                  <StuiSelect className="stui-field-input" value={newTask.owner || ''}
                    onChange={e => setNewTask(prev => ({ ...prev, owner: e.target.value || null }))}>
                    <option value="">Unassigned</option>
                    {roster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
                  </StuiSelect>
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Originator</label>
                  <StuiSelect className="stui-field-input" value={newTask.originator || ''}
                    onChange={e => setNewTask({ ...newTask, originator: e.target.value || null })}>
                    <option value="">Unassigned</option>
                    {originatorRoster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
                  </StuiSelect>
                </div>

                <div className="stui-field-group stui-col-12">
                  <label>Type</label>
                  <StuiSelect className="stui-field-input" value={newTask.entryType || 'task'}
                    onChange={e => setNewTask({ ...newTask, entryType: e.target.value })}>
                    {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </StuiSelect>
                </div>

                <div className="stui-field-group stui-col-12">
                  <label>Priority</label>
                  <StuiSelect className="stui-field-input" value={newTask.category || ''}
                    onChange={e => setNewTask({ ...newTask, category: e.target.value || null })}>
                    <option value="">Untriaged</option>
                    {priorityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </StuiSelect>
                </div>

                <div className="stui-field-group stui-col-24">
                  <label>Source</label>
                  <input type="text" className="stui-field-input" value={newTask.source}
                    onChange={e => setNewTask({ ...newTask, source: e.target.value })}
                    placeholder="Where this task came from" />
                </div>

                <div className="stui-col-24">
                  <div className="stui-detail-section-header">Details</div>
                </div>

                <div className="stui-field-group stui-col-24">
                  <textarea className="stui-field-input resizable" value={newTask.details}
                    onChange={e => setNewTask({ ...newTask, details: e.target.value })}
                    placeholder="Requirements, context, notes..." rows={6} />
                </div>

              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setShowAddTask(false)}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleAddTask} disabled={saving || !newTask.task.trim()}>
                {saving ? 'Adding...' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Details popup (datasheet double-click) ── */}
      {showDescModal && descModalTask && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3>Task Details — #{descModalTask.id} — {descModalTask.task}</h3>
              <button className="stui-modal-close" onClick={() => setShowDescModal(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-field-group">
                <label>Details</label>
                {dsEditMode ? (
                  <textarea className="stui-field-input resizable" value={descLocal.details} onChange={e => setDescLocal(p => ({ ...p, details: e.target.value }))} rows={16} autoFocus />
                ) : (
                  <textarea className="stui-field-input resizable" value={descLocal.details || ''} readOnly rows={16} onChange={() => {}} />
                )}
              </div>
            </div>
            {dsEditMode && (
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary" onClick={() => setShowDescModal(false)}>Cancel</button>
                <button className="stui-btn stui-btn-primary" onClick={handleDescApply}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showTransfer && <TaskTransferModal onClose={() => setShowTransfer(false)} onTransferred={loadTasks} />}
      <AdvancedSearchModal isOpen={showAdvSearch} onClose={() => setShowAdvSearch(false)} query={searchTerm} fields={SEARCH_FIELD_LABELS} onApply={setSearchTerm} />
    </div>
  );
};

export default TaskLogPage;
